import { i18n } from '@fs/zion-locale'
import * as Yup from 'yup'
import { NO_RELATION_TYPE } from './saveRelationships'

const relationshipsShape = Yup.array().of(
  Yup.object().shape({
    relationship: Yup.object().required(i18n.t('FormValidation.notEmpty', 'Field cannot be empty.')),
  })
)

// Use to validate the Household Details form in the main card
export const householdDetailsValidationSchema = Yup.object().shape({
  household: Yup.object().nullable(),
  relationships: Yup.array().of(
    Yup.object()
      .test(
        'no-duplicate-relations',
        i18n.t('FormValidation.duplicateRelationship', 'A relationship with this person has already been added.'),
        (data, context) => {
          // Get the most parent context to access the full form data
          const formData = context.from.at(-1)?.value || {}
          const relationships = formData?.relationships || []
          // Get the current personId from the form data or fallback to the value key from the autosuggest value
          const currentPersonId = data?.personId || data?.value?.key
          const currentIndex = context?.options?.index ?? null
          if (!currentPersonId) return true

          // Find all indices where the currentPersonId appears
          const indices = relationships
            .map((rel, idx) => (rel?.value?.key === currentPersonId ? idx : -1))
            .filter((idx) => idx !== -1)

          // Get the current index from the context path, using regex to extract the index from the path string as a fallback
          let currentIdx = -1
          if (currentIndex) {
            currentIdx = currentIndex
          } else {
            const match = /\[(\d+)\]/.exec(context.path)
            currentIdx = match ? Number.parseInt(match[1], 10) : -1
          }

          // Only show error if this is NOT the first occurrence
          if (indices.length > 1 && indices.indexOf(currentIdx) !== 0) {
            return context.createError({
              path: `${context.path}.value`,
              message: i18n.t(
                'FormValidation.duplicateRelationship',
                'A relationship with this person has already been added.'
              ),
            })
          }
          return true
        }
      )
      .test(
        'relationship-completeness',
        i18n.t(
          'FormValidation.incompleteRelationship',
          'Please select both a person and relationship type, or remove this row.'
        ),
        (data, context) => {
          // Skip deleted relationships
          if (data?.isDeleted) return true
          const relationshipType = data?.relationshipType
          const hasPersonSelected = Boolean(data?.personId || data?.value?.key)

          // Both empty is OK (user added row but didn't fill it)
          if (!relationshipType && !hasPersonSelected) return true

          // NO_RELATION_TYPE without person is also OK (treated as empty placeholder)
          if (relationshipType === NO_RELATION_TYPE && !hasPersonSelected) return true

          // CRITICAL FIX: NO_RELATION_TYPE with a person is a VALID selection
          // This represents intentional "no familial relationship" (e.g., boarders, servants)
          if (relationshipType === NO_RELATION_TYPE && hasPersonSelected) return true

          // Any other relationship type with a person is OK
          const hasRelationshipType = Boolean(relationshipType) && relationshipType !== NO_RELATION_TYPE
          if (hasRelationshipType && hasPersonSelected) return true

          // One filled but not the other = incomplete
          return context.createError({
            path: `${context.path}.value`,
            message: i18n.t(
              'FormValidation.incompleteRelationship',
              'Please select both a person and relationship type, or remove this row.'
            ),
          })
        }
      )
  ),
})

// Use to validate the Previous Relationships dialog form
export const previousRelationshipsValidationSchema = Yup.object().shape({
  relationships: relationshipsShape,
})
